import React, { useCallback, useState, JSX } from 'react'

import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props'
import { useRouter } from 'solito/router'

import { useSendGoogleAnalyticsEvent } from 'app/hooks/useSendGoogleAnalyticsEvent'
import ErrorHandler, { isErrorHandlerType } from 'app/services/ErrorHandler'
import {
  useFacebookLoginMutation,
  useFacebookRegisterMutation
} from 'app/services/Query/user/user.mutation'
import useAuthStore, { getAuthActions } from 'app/store/auth.store'

import FacebookRenderButton from './FacebookRenderButton'

interface FacebookButtonProps {
  variant?: 'register' | 'login'
  onSuccess?: () => void
}

const FacebookButton: RFC<FacebookButtonProps> = ({
  variant = 'login',
  onSuccess
}) => {
  const { push } = useRouter()
  const actions = useAuthStore(getAuthActions)
  const facebookLogin = useFacebookLoginMutation()
  const facebookRegister = useFacebookRegisterMutation()
  const { sendGA4Event } = useSendGoogleAnalyticsEvent()

  const [_, setLoading] = useState<boolean>(false)

  const handleSuccess = useCallback(async ({ accessToken }: any) => {
    try {
      setLoading(true)

      if (variant === 'register') {
        const resp = await facebookRegister.mutateAsync({
          user: {
            access_token: accessToken
          }
        })

        actions.authorize(resp.jwt_token)

        sendGA4Event({ eventName: 'sign_up', method: 'facebook' })

        push('/sign-up/successfully-created')
      } else {
        const resp = await facebookLogin.mutateAsync({
          authentication: {
            access_token: accessToken,
            keep_me_logged_in: true
          }
        })

        await actions.authorize(resp.jwt_token)

        sendGA4Event({ eventName: 'login', method: 'facebook' })

        if (onSuccess) onSuccess()
      }
    } catch (error) {
      if (isErrorHandlerType(error)) {
        new ErrorHandler(error.data)
      }

      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [])

  return (
    <FacebookLogin
      appId={process.env.FACEBOOK_APP_ID}
      callback={handleSuccess}
      fields="name,email"
      disableMobileRedirect
      render={(renderProps): JSX.Element => (
        <FacebookRenderButton
          onClick={renderProps.onClick}
          disabled={renderProps.isDisabled || !renderProps.isSdkLoaded}
        />
      )}
    />
  )
}

export default FacebookButton
